import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Button } from './ui/button';
import { Clock, LogOut } from 'lucide-react';

export function InactivityWarning() {
  const navigate = useNavigate();
  const { showInactivityWarning, secondsUntilLogout, stayLoggedIn, logout, isAuthenticated } = useAuth();

  // Redirect to auth page when logged out due to inactivity
  useEffect(() => {
    const handleAuthChange = (e) => {
      if (e.detail?.reason === 'inactivity') {
        navigate('/auth', { state: { message: 'You have been logged out due to inactivity.' } });
      }
    };
    
    window.addEventListener('auth-change', handleAuthChange);
    return () => window.removeEventListener('auth-change', handleAuthChange);
  }, [navigate]);

  const handleLogoutNow = () => {
    console.log('Log out now clicked');
    logout();
  };

  if (!isAuthenticated || !showInactivityWarning) return null;

  return (
    <AlertDialog open={showInactivityWarning}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-yellow-500/10 rounded-full">
              <Clock className="w-6 h-6 text-yellow-500" />
            </div>
            <AlertDialogTitle className="text-xl font-serif">Session Timeout Warning</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="text-base">
            You will be automatically logged out in{' '}
            <span className="font-bold text-foreground">{secondsUntilLogout} seconds</span>{' '}
            due to inactivity.
          </AlertDialogDescription>
        </AlertDialogHeader>
        
        <div className="my-4 p-4 bg-muted/50 rounded-lg">
          <div className="flex items-center justify-center">
            <div className="text-center">
              <div className="text-4xl font-bold text-primary">{secondsUntilLogout}</div>
              <div className="text-sm text-muted-foreground">seconds remaining</div>
            </div>
          </div>
          <div className="mt-3 w-full bg-muted rounded-full h-2 overflow-hidden">
            <div 
              className="bg-yellow-500 h-full transition-all duration-1000 ease-linear"
              style={{ width: `${(secondsUntilLogout / 60) * 100}%` }}
            />
          </div>
        </div>

        <AlertDialogFooter className="gap-2 sm:gap-0">
          <Button 
            variant="outline"
            onClick={handleLogoutNow}
            className="rounded-full"
            data-testid="logout-now-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Log Out Now
          </Button>
          <AlertDialogAction 
            onClick={stayLoggedIn}
            className="rounded-full"
            data-testid="stay-logged-in-btn"
          >
            Stay Logged In
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
